/**
 * <CameraArrival> — ma vignette qui glisse et se pose dans la barre du live (privé à `<LiveControlBar>`).
 *
 * Au téléphone, en passant de la visio à l'activité, ma caméra quitte son coin de la scène et se pose
 * en bas, dans le bouton caméra de la barre (Wajih, 19/10). Elle arrive d'au-dessus de la barre, plus
 * grande, et se pose à la taille du bouton, à la durée `entree` et à la courbe de la marque
 * (`theme.motion`). Elle ne glisse qu'à un passage : déjà là quand le live s'ouvre, elle est posée.
 * En mouvement réduit, rien ne bouge.
 */
import React, { useEffect, useRef } from "react"
import { Animated, Easing, Platform } from "react-native"
import { useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"

/** D'où elle part : au-dessus de la barre, à peu près où se tenait la vignette de la scène. */
const FROM_Y = -72
const FROM_SCALE = 1.6

export function CameraArrival(props: { animate: boolean; children: React.ReactNode }) {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    // Lu au montage seulement : c'est le passage qui la fait glisser, pas un rendu suivant.
    const animateRef = useRef(props.animate)
    const progress = useRef(new Animated.Value(animateRef.current ? 0 : 1)).current

    useEffect(() => {
        if (!animateRef.current) return undefined
        if (reduceMotion) {
            progress.setValue(1)
            return undefined
        }
        const arrival = Animated.timing(progress, {
            toValue: 1,
            duration: theme.motion.entree,
            easing: Easing.bezier(...theme.motion.easing),
            useNativeDriver: Platform.OS !== "web",
        })
        arrival.start()
        return () => arrival.stop()
    }, [progress, reduceMotion, theme.motion])

    return (
        <Animated.View
            style={{
                transform: [
                    { translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [FROM_Y, 0] }) },
                    { scale: progress.interpolate({ inputRange: [0, 1], outputRange: [FROM_SCALE, 1] }) },
                ],
            }}
        >
            {props.children}
        </Animated.View>
    )
}
